import i18next from 'i18next';

import { CHATBOT_OPTIONS_TYPE } from "../../../constants/AppConstants";

export const uuid = () => new Date().getTime().toString(36) + Math.random().toString(36).slice(2);

const OPTION_COLLECTION_FIELDS = ['chat_bot_actions', 'options_list', 'option_sub_lists'];

export const getOptionCollection = (option, field) => (
  Array.isArray(option?.[field]) ? option[field] : []
);

export const indexChatbotOptionsById = (options = []) => {
  const optionsById = new Map();

  (Array.isArray(options) ? options : []).forEach((option) => {
    if (option?.id != null) optionsById.set(option.id, option);
  });

  return optionsById;
};

/**
 * Nested `options_list` items from the microservice may omit collections that
 * the same records still have on the top-level index payload. Copy those
 * arrays in before we default missing fields to `[]`.
 */
export const hydrateNestedOptionFromIndex = (nestedOption, optionsById) => {
  if (!nestedOption || nestedOption.id == null || !optionsById) return nestedOption;

  const fullOption = typeof optionsById.get === 'function'
    ? optionsById.get(nestedOption.id)
    : optionsById[nestedOption.id];

  if (!fullOption) return nestedOption;

  const hydrated = { ...fullOption, ...nestedOption };

  OPTION_COLLECTION_FIELDS.forEach((field) => {
    if (!Array.isArray(nestedOption[field]) && Array.isArray(fullOption[field])) {
      hydrated[field] = fullOption[field];
    }
  });

  return hydrated;
};

const wasCollectionLoaded = (option, field) => {
  const loadedFlag = `${field}_loaded`;

  if (Object.prototype.hasOwnProperty.call(option, loadedFlag)) {
    return option[loadedFlag];
  }

  return Array.isArray(option[field]);
};

/**
 * The microservice chatbot options endpoint may omit collection fields on
 * children (e.g. menu options without `chat_bot_actions`). Normalize them so
 * every consumer can safely call array methods, and remember whether the
 * original payload actually included `chat_bot_actions`.
 */
export const normalizeChatbotOption = (option) => {
  if (!option || typeof option !== 'object' || Array.isArray(option)) return option;

  const chatBotActionsLoaded = wasCollectionLoaded(option, 'chat_bot_actions');

  const normalized = OPTION_COLLECTION_FIELDS.reduce(
    (acc, field) => ({
      ...acc,
      [field]: Array.isArray(option[field]) ? option[field] : []
    }),
    { ...option }
  );

  normalized.chat_bot_actions_loaded = chatBotActionsLoaded;
  normalized.options_list = normalized.options_list.map((child) => normalizeChatbotOption(child));

  return normalized;
};

export const prepareChatbotOptionForEdit = (option, extra = {}) => {
  const chatBotActions = getOptionCollection(option, 'chat_bot_actions');

  return normalizeChatbotOption({
    ...option,
    ...extra,
    originalAttachedFile: extra.originalAttachedFile ?? option?.attached_file,
    has_step_actions: extra.has_step_actions ?? chatBotActions.length > 0,
    jump_to_option: extra.jump_to_option ?? (
      option?.option_type === CHATBOT_OPTIONS_TYPE.JUMP_TO
        ? chatBotActions[0]?.jump_option_id ?? null
        : option?.jump_to_option ?? null
    )
  });
};

/**
 * If the GET omitted `chat_bot_actions` and the user did not edit actions,
 * skip nested attributes so a save cannot wipe records the UI never loaded.
 */
export const shouldOmitChatBotActionsAttributes = ({
  hasStepActions,
  actions,
  actionsLoaded
} = {}) => {
  if (hasStepActions) return false;
  if (actionsLoaded !== false) return false;

  const safeActions = Array.isArray(actions) ? actions : [];
  return safeActions.every((action) => !action?.id);
};

export const hasChildren = (nodes = [], nodeId = '') => {
  const children = nodes.filter((node) => node.source === nodeId && node.target !== nodeId);
  return children.length > 0;
};

export const iconMapping = {
  [CHATBOT_OPTIONS_TYPE.QUESTION]: { icon: 'mi-question-mark-circle-outline', iconName: 'question-mark-circle-outline', text: i18next.t('chatbot.config.options.question') },
  [CHATBOT_OPTIONS_TYPE.MENU]: { icon: 'mi-menu-fill', iconName: 'menu-fill', text: i18next.t('chatbot.config.options.menu') },
  [CHATBOT_OPTIONS_TYPE.MESSAGE]: { icon: 'mi-message-square-outline', iconName: 'message-square-outline', text: i18next.t('chatbot.config.options.message') },
  [CHATBOT_OPTIONS_TYPE.JUMP_TO]: { icon: 'mi-link-2-outline', iconName: 'link-2-outline', text: i18next.t('chatbot.config.options.go_to') },
  [CHATBOT_OPTIONS_TYPE.ACTIVE_MIA]: { icon: 'mi-outline-robot-one', iconName: 'bot', text: i18next.t('chatbot.config.options.active_mia') },
  [CHATBOT_OPTIONS_TYPE.API]: { icon: 'mi-outline-robot-one', iconName: 'dynamic-form', text: i18next.t('chatbot.config.options.api') },
  [CHATBOT_OPTIONS_TYPE.RESOLVE_CHAT]: { icon: 'mi-message-check', iconName: 'message_check', text: i18next.t('chatbot.config.options.resolve_chat_caption') },
  [CHATBOT_OPTIONS_TYPE.WHATSAPP_FLOW]: { icon: 'mi-whatsapp-line', iconName: 'whatsapp-outline', text: i18next.t('chatbot.config.options.whatsapp_flow') }
};

/**
 * ¿El flujo corre solo en TikTok?
 *
 * TikTok no tiene mensajes interactivos: su adaptador de envío
 * (`tiktok-message.adapter.ts`) manda únicamente `messageType: "TEXT"` con el cuerpo del
 * mensaje y descarta `interactiveOptions` —solo el handler de Facebook las convierte en
 * botones o quick replies—. Elegir "Botones" o "Lista de opciones" en un flujo de TikTok
 * produce un mensaje sin opciones a la vista y deja al contacto sin forma de elegir.
 *
 * Con "Números" el menú sí funciona: las opciones van en el texto que escribe el
 * comercio y `MatchChatbotOptionService` resuelve la respuesta por posición, texto
 * literal o similitud de trigramas.
 *
 * Se exige que ningún otro canal esté activo (clásico o multipágina): si el mismo
 * flujo también corre en WhatsApp, Messenger o Instagram, ahí los interactivos sí
 * se renderizan y hay que ofrecerlos.
 */
export const isTikTokOnlyChatbot = (chatbot = {}) => (
  !!chatbot.tiktok_enabled
    && !chatbot.whatsapp_enabled
    && !chatbot.messenger_enabled
    && !chatbot.instagram_enabled
    && !chatbot.messenger_multipage_enabled
    && !chatbot.instagram_multipage_enabled
);

const MEDIA_CHANNEL_ORDER = ['whatsapp', 'messenger', 'instagram'];

/**
 * Canales relevantes para validar/adjuntar media (clásico O multipágina).
 * @param {Object} [chatbot]
 * @returns {string[]}
 */
export const getEnabledMediaChannels = (chatbot = {}) => MEDIA_CHANNEL_ORDER.filter((channel) => {
  if (channel === 'whatsapp') return !!chatbot.whatsapp_enabled;
  if (channel === 'messenger') {
    return !!(chatbot.messenger_enabled || chatbot.messenger_multipage_enabled);
  }
  if (channel === 'instagram') {
    return !!(chatbot.instagram_enabled || chatbot.instagram_multipage_enabled);
  }
  return false;
});

/**
 * Instagram (clásico o multipágina) no admite documentos en el flujo.
 * @param {Object} [chatbot]
 * @returns {boolean}
 */
export const chatbotDisallowsDocuments = (chatbot = {}) => !!(
  chatbot.instagram_enabled || chatbot.instagram_multipage_enabled
);
